import { z } from 'zod';
import { CommsChannel } from '../enums.js';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS10 — Comms usage → SaaS invoice line items (brief 10 §2; ADR-0010 §7; ADR-0011 §2).
 *
 * Salonomy bills a salon for SMS/WhatsApp it sent BEYOND its pooled allowance — but ONLY where the
 * salon explicitly chose `atCapPolicy === 'auto_top_up'`. The pause-by-default hard rule from
 * FOUNDATION/`allowance.ts` is ABSOLUTE: a pause/ask salon generates ZERO overage, ever — no opt-in,
 * no charge. Overage is metered IN ARREARS from the per-message audit log (`comms_messages`) at the
 * published 8p rate, never prorated.
 *
 * Every overage charge DRILLS DOWN to the exact logged messages it represents (ADR-0010 §7 — "if we
 * cannot show the receipts, we cannot bill it"): the breakdown carries the audit message ids the count
 * was computed from, recomputed by replaying the SAME allowance model the send pipeline used — NOT a
 * parallel meter. Money is integer pence; the rate is the published `SMS_OVERAGE_PENCE` (8p default).
 */

/** One traced overage message: the audit row a billable overage unit drills to. */
export const OverageMessage = z
  .object({
    messageId: Uuid.describe('comms_messages.id — the per-message audit receipt'),
    channel: CommsChannel,
    /** Billable overage units this message contributed (SMS segments past the pool; 1 for whatsapp). */
    overageUnits: z.int().min(1),
    sentAt: IsoDateTime.nullable(),
  })
  .meta({ id: 'OverageMessage' });
export type OverageMessage = z.infer<typeof OverageMessage>;

/** Per-channel overage rollup (sms | whatsapp) — the included pool and the part that spilled over. */
export const OverageChannelBreakdown = z
  .object({
    channel: z.enum(['sms', 'whatsapp']),
    /** Included pooled allowance for this channel (perChair × billable chairs) over the period. */
    includedUnits: z.int().min(0),
    /** Metered units sent this period (in-pool + overage). */
    meteredUnits: z.int().min(0),
    /** Units billed as overage (metered beyond the pool). 0 for pause/ask salons, always. */
    overageUnits: z.int().min(0),
    ratePence: Pence.describe('published per-unit overage rate (8p default)'),
    amountPence: Pence,
    /** The audit messages this channel's overage units drill down to (traceable receipts). */
    messages: z.array(OverageMessage),
  })
  .meta({ id: 'OverageChannelBreakdown' });
export type OverageChannelBreakdown = z.infer<typeof OverageChannelBreakdown>;

/**
 * The comms-overage view for a billing period: the "why is this comms line on my invoice" receipts.
 * `billable` is true ONLY for an `auto_top_up` salon — a pause/ask salon returns billable=false and
 * zero everywhere, with no message ids (it is never charged for comms).
 */
export const CommsOveragePreview = z
  .object({
    /** True ONLY where atCapPolicy === 'auto_top_up'. pause/ask → false and zero everywhere. */
    billable: z.boolean(),
    atCapPolicy: z.enum(['pause', 'ask', 'auto_top_up']),
    periodStart: IsoDateTime,
    periodEnd: IsoDateTime,
    ratePence: Pence,
    /** Total billable overage units across channels (0 unless billable). */
    overageUnits: z.int().min(0),
    /** Total billable overage amount in pence (overageUnits × ratePence; never prorated). */
    amountPence: Pence,
    channels: z.array(OverageChannelBreakdown),
  })
  .meta({ id: 'CommsOveragePreview' });
export type CommsOveragePreview = z.infer<typeof CommsOveragePreview>;

/**
 * Query the comms-overage preview for an explicit window (defaults to the current billing period).
 * No `.meta({ id })` — this is a querystring schema; registering an id would make @fastify/swagger emit
 * it as a top-level parameter `$ref` it cannot resolve (matches every other *Query schema's convention).
 */
export const CommsOverageQuery = z.object({
  periodStart: IsoDateTime.optional().describe(
    'window start (defaults to subscription period start)',
  ),
  periodEnd: IsoDateTime.optional().describe('window end (defaults to now)'),
});
export type CommsOverageQuery = z.infer<typeof CommsOverageQuery>;
